<template>
  <div class="robot-detail">
    <div class="detail-content">
      <!-- 左侧列 -->
      <div class="left-column">
        <!-- 左上角 - 电机模块 -->
        <div class="panel top-left">
          <motor-module :param-data="robotData" />
        </div>
        
        <!-- 左下角 - 相机模块 -->
        <div class="panel bottom-left">
          <camera-module :param-data="robotData" />
        </div>
      </div>
      
      <!-- 中左列 - CPU和内存 -->
      <div class="center-left-column">
        <!-- CPU模块 -->
        <div class="panel top-center-left">
          <cpu-module :cpu-data="cpuData" />
        </div>
        
        <!-- 内存模块 -->
        <div class="panel bottom-center-left">
          <memory-module :memory-data="memoryData" :disk-data="diskData" />
        </div>
      </div>
      
      <!-- 中心区域 -->
      <div class="panel center-panel">
        <robot-globe :robot-data="robotGlobeData" />
      </div>
      
      <!-- 右中列 - 里程与甲烷状态 -->
      <div class="center-right-column">
        <!-- 上半部分 - 里程与甲烷状态模块 -->
        <div class="panel top-center-right">
          <mileage-status-module :mileage-data="mileageData" :methane-data="methaneData" />
        </div>
        <!-- 下半部分 - 碰撞检测模块 -->
        <div class="panel bottom-center-right" v-if="hasCollisionData">
          <collision-module :collision-data="collisionData" />
        </div>
      </div>
      
      <!-- 右侧列 -->
      <div class="right-column">
        <!-- 右上角 - 电池与传感器 -->
        <div class="panel top-right">
          <battery-sensor-module :param-data="robotData" />
        </div>
        
        <!-- 右下角 - 网卡信息 -->
        <div class="panel bottom-right">
          <network-card-module :network-data="networkCardData" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RobotGlobe from './RobotGlobe.vue'
import MotorModule from './MotorModule.vue'
import CameraModule from './CameraModule.vue'
import BatterySensorModule from './BatterySensorModule.vue'
import NetworkCardModule from './NetworkCardModule.vue'
import CpuModule from './CpuModule.vue'
import MemoryModule from './MemoryModule.vue'
import MileageStatusModule from './MileageStatusModule.vue'
import CollisionModule from './CollisionModule.vue'

export default {
  name: 'RobotDetail',
  components: {
    RobotGlobe,
    MotorModule,
    CameraModule,
    BatterySensorModule,
    NetworkCardModule,
    CpuModule,
    MemoryModule,
    MileageStatusModule,
    CollisionModule
  },
  props: {
    robotData: {
      type: Array,
      default: () => []
    },
    networkCardData: {
      type: Object,
      default: () => ({ nicList: [] })
    },
    cpuData: {
      type: Object,
      default: () => ({})
    },
    memoryData: {
      type: Object,
      default: () => ({})
    },
    diskData: {
      type: Object,
      default: () => ({})
    },
    mileageData: {
      type: Object,
      default: () => ({})
    },
    methaneData: {
      type: Object,
      default: () => ({})
    },
    collisionData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      robotGlobeData: {
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        ballTemperature: null,
        ballTemperatureUnit: '℃',
        ballTemperatureNameCn: '',
        ballTemperatureNameEn: ''
      }
    }
  },
  watch: {
    robotData: {
      immediate: true,
      handler(newData) {
        const ballTempItem = (newData || []).find(item =>
          item &&
          item.deviceName === 'motor_master' &&
          item.paramName === 'ball_temperature'
        )

        this.robotGlobeData = {
          ...this.robotGlobeData,
          ballTemperature: ballTempItem ? ballTempItem.data : null,
          ballTemperatureUnit: ballTempItem ? (ballTempItem.paramUnit || '℃') : '℃',
          ballTemperatureNameCn: ballTempItem ? ballTempItem.paramNameCn : '',
          ballTemperatureNameEn: ballTempItem ? ballTempItem.paramNameEn : ''
        }
      }
    }
  },
  computed: {
    hasCollisionData() {
      return this.collisionData && Object.keys(this.collisionData).length > 0;
    }
  }
}
</script>

<style lang="less" scoped>
.robot-detail {
  width: 100%;
  height: ~"calc(100% - 140px)";
  position: relative;
  overflow: hidden;
  
  .detail-content {
    width: 100%;
    height: 100%;
    display: flex;
    gap: 16px;
    box-sizing: border-box;

    @media screen and (max-width: 1366px) {
      gap: 12px;
    }

    .panel {
      background: transparent;
      border: none;
      border-radius: 0;
      backdrop-filter: none;
      box-shadow: none;
      padding: 0;
      position: relative;
      overflow: hidden;
      min-height: 0;
      
      &::before {
        display: none;
      }
    }
    
    // 左侧列
    .left-column {
      width: 320px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      
      @media screen and (max-width: 1366px) {
        width: 25%;
        min-width: 260px;
        gap: 12px;
      }
    }
    
    // 中左列 - CPU和内存
    .center-left-column {
      width: 320px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      
      @media screen and (max-width: 1366px) {
        width: 25%;
        min-width: 260px;
        gap: 12px;
      }
    }
    
    // 右中列 - 里程状态
    .center-right-column {
      width: 320px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      
      @media screen and (max-width: 1366px) {
        width: 25%;
        min-width: 260px;
        gap: 12px;
      }
    }
    
    // 右侧列
    .right-column {
      width: 350px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      
      @media screen and (max-width: 1366px) {
        width: 25%;
        min-width: 260px;
        gap: 12px;
      }
    }

    .top-left,
    .bottom-left,
    .top-center-left,
    .bottom-center-left,
    .top-right,
    .bottom-right,
    .top-center-right,
    .bottom-center-right {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
      
      // 当内部模块收缩时，panel也应该收缩
      &:has(.collapsed) {
        flex: 0 0 auto;
      }
    }

    .center-panel {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}
</style>
